import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  X,
  Plus,
  Loader2,
  CalendarRange,
  Banknote,
  ChevronRight,
  ChevronLeft,
  ClipboardList,
  Target,
  Send,
  Save,
  Pencil,
  Trash2,
  Paperclip,
  Check,
  RotateCcw,
  Upload,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import PlanDetailModal from "./PlanDetailModal";
import { createPlan } from "../../../api/hrm/plans/info";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
  showInfoToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { numberToVietnameseText } from "../../../utils/vietnameseNumberWords";
import {
  PLAN_CONTENT_MAX_LENGTH,
  validatePlanPdfFile,
} from "./planFileConstraints";

const STEPS = [
  {
    id: 0,
    title: "Thông tin cơ bản",
    pill: "THÔNG TIN",
    hint: "Tiêu đề, phân loại, thời gian và ngân sách",
  },
  {
    id: 1,
    title: "Nội dung & tệp",
    pill: "NỘI DUNG",
    hint: "Mô tả kế hoạch và tài liệu đính kèm (nếu có)",
  },
  {
    id: 2,
    title: "Mục tiêu chi tiết",
    pill: "MỤC TIÊU",
    hint: "Ít nhất một mục tiêu có người phụ trách",
  },
  {
    id: 3,
    title: "Xác nhận & gửi",
    pill: "XÁC NHẬN",
    hint: "Kiểm tra lại trước khi lưu nháp hoặc gửi phê duyệt",
  },
];

const parseBudgetDigits = (formatted) => (formatted || "").replace(/[^\d]/g, "");

const PlanCreateModal = ({ isOpen, onClose, onSaved }) => {
  const planMainFileRef = useRef(null);
  const mainDragDepthRef = useRef(0);
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [budget, setBudget] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mainFileDragOver, setMainFileDragOver] = useState(false);

  const defaultExecutorName = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return String(u.name || u.employee_name || u.full_name || u.username || "").trim();
    } catch {
      return "";
    }
  }, []);

  const resetForm = useCallback(() => {
    setStep(0);
    setTitle("");
    setContent("");
    setStartDate("");
    setEndDate("");
    setType("");
    setCategory("");
    setBudget("");
    setAttachment(null);
    setDetailItems([]);
    setEditingItem(null);
    setShowDetailModal(false);
    mainDragDepthRef.current = 0;
    setMainFileDragOver(false);
    if (planMainFileRef.current) planMainFileRef.current.value = "";
  }, []);

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen, resetForm]);

  const assignMainAttachment = useCallback((file) => {
    if (!file) {
      setAttachment(null);
      if (planMainFileRef.current) planMainFileRef.current.value = "";
      return;
    }
    const v = validatePlanPdfFile(file);
    if (!v.ok) {
      showErrorToast(v.message || "Tệp không hợp lệ.");
      if (planMainFileRef.current) planMainFileRef.current.value = "";
      return;
    }
    setAttachment(file);
    try {
      if (planMainFileRef.current) {
        const dt = new DataTransfer();
        dt.items.add(file);
        planMainFileRef.current.files = dt.files;
      }
    } catch {
      /* một số trình duyệt không cho gán FileList */
    }
  }, []);

  const handleMainPlanFileChange = (e) => {
    assignMainAttachment(e.target.files?.[0] || null);
  };

  const onMainDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    mainDragDepthRef.current += 1;
    setMainFileDragOver(true);
  };

  const onMainDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    mainDragDepthRef.current -= 1;
    if (mainDragDepthRef.current <= 0) {
      mainDragDepthRef.current = 0;
      setMainFileDragOver(false);
    }
  };

  const onMainDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = "copy";
    } catch {
      /* ignore */
    }
  };

  const onMainDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    mainDragDepthRef.current = 0;
    setMainFileDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) assignMainAttachment(f);
  };

  const hasDraftInput = useCallback(() => {
    if (step > 0) return true;
    if (title.trim()) return true;
    if (content.trim()) return true;
    if (startDate || endDate) return true;
    if (type || category) return true;
    const b = parseBudgetDigits(budget);
    if (b && Number(b) > 0) return true;
    if (attachment) return true;
    if (detailItems.length > 0) return true;
    return false;
  }, [
    step,
    title,
    content,
    startDate,
    endDate,
    type,
    category,
    budget,
    attachment,
    detailItems.length,
  ]);

  const requestClose = useCallback(async () => {
    if (loading) return;
    if (!hasDraftInput()) {
      onClose?.();
      return;
    }
    const res = await showConfirmDialog({
      title: "Bỏ qua nội dung đang nhập?",
      text: "Dữ liệu chưa lưu sẽ mất. Bạn có chắc muốn đóng form?",
      confirmText: "Đóng form",
      cancelText: "Tiếp tục nhập",
    });
    if (res.isConfirmed) onClose?.();
  }, [loading, hasDraftInput, onClose]);

  const handleClearForm = useCallback(async () => {
    if (loading) return;
    if (!hasDraftInput()) return;
    const res = await showConfirmDialog({
      title: "Làm sạch form?",
      text: "Toàn bộ nội dung đã nhập sẽ bị xoá và bạn bắt đầu lại từ bước 1.",
      confirmText: "Làm sạch",
      cancelText: "Không",
    });
    if (!res.isConfirmed) return;
    resetForm();
    showInfoToast("Đã làm sạch form.");
  }, [loading, hasDraftInput, resetForm]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key !== "Escape" || loading) return;
      if (showDetailModal) return;
      requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, loading, showDetailModal, requestClose]);

  const formatCurrencyInput = (value) => {
    const numericValue = value.replace(/[^\d]/g, "");
    return Number(numericValue).toLocaleString("vi-VN");
  };

  const formatCurrencyDisplay = (formatted) => {
    const n = parseBudgetDigits(formatted || "0");
    if (!n) return "0 ₫";
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(Number(n));
  };

  const validateStep = (s) => {
    if (s === 0) {
      if (!title.trim()) {
        showErrorToast("Vui lòng nhập tiêu đề kế hoạch.");
        return false;
      }
      if (!startDate || !endDate) {
        showErrorToast("Vui lòng chọn khoảng thời gian.");
        return false;
      }
      if (new Date(endDate) < new Date(startDate)) {
        showErrorToast("Ngày kết thúc không được nhỏ hơn ngày bắt đầu.");
        return false;
      }
      if (!type || !category) {
        showErrorToast("Vui lòng chọn phân loại và loại kế hoạch.");
        return false;
      }
      return true;
    }
    if (s === 1) {
      if (!content.trim()) {
        showErrorToast("Vui lòng nhập nội dung tổng quan.");
        return false;
      }
      if (content.length > PLAN_CONTENT_MAX_LENGTH) {
        showErrorToast(`Nội dung tổng quan tối đa ${PLAN_CONTENT_MAX_LENGTH} ký tự.`);
        return false;
      }
      return true;
    }
    if (s === 2) {
      if (detailItems.length === 0) {
        showErrorToast("Vui lòng thêm ít nhất một mục tiêu chi tiết.");
        return false;
      }
      return true;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const goBack = () => setStep((prev) => Math.max(prev - 1, 0));

  const handleSave = async (mode = "draft") => {
    if (!validateStep(0) || !validateStep(1) || !validateStep(2)) {
      showErrorToast("Vui lòng hoàn thành đầy đủ các bước trước.");
      return;
    }

    const userStr = localStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;

    if (!user?.id || !user?.branch || !user?.department_id) {
      showErrorToast("Thông tin người dùng không hợp lệ.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("content", content.trim());
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("plan_type", type);
      formData.append("plan_category", category);
      formData.append("status", mode);
      formData.append("creator_id", user.id);

      const branchId = typeof user.branch === "string" ? user.branch : user.branch?.value || null;
      const departmentId = user.department_id || null;
      formData.append("branch_id", branchId);
      formData.append("department_id", departmentId);
      formData.append("assigned_to", departmentId);

      if (budget) formData.append("budget", parseBudgetDigits(budget));

      if (attachment instanceof File) {
        const av = validatePlanPdfFile(attachment);
        if (!av.ok) {
          showErrorToast(av.message || "Tệp đính kèm không hợp lệ.");
          setLoading(false);
          return;
        }
        formData.append("attachments[]", attachment);
      }
      for (const i of detailItems) {
        if (i.attachment instanceof File) {
          const v = validatePlanPdfFile(i.attachment);
          if (!v.ok) {
            showErrorToast(
              `Tệp mục tiêu "${(i.goal || "").slice(0, 48)}${(i.goal || "").length > 48 ? "…" : ""}": ${v.message}`
            );
            setLoading(false);
            return;
          }
        }
      }
      const details = detailItems.map((i) => {
        if (i.attachment instanceof File) {
          formData.append("attachments[]", i.attachment);
        }
        return {
          target: i.goal,
          assignee: i.owner,
          attachment: i.attachment?.name || null,
        };
      });
      formData.append("details", JSON.stringify(details));

      await createPlan(formData);

      showSuccessToast(
        mode === "waiting_approval" ? "Gửi phê duyệt thành công" : "Lưu kế hoạch thành công"
      );
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Lỗi khi lưu kế hoạch:", error);
      showErrorToast("Lỗi khi tạo kế hoạch");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDetail = (item) => {
    if (editingItem) {
      setDetailItems((prev) => prev.map((i) => (i.id === editingItem.id ? item : i)));
      setEditingItem(null);
    } else {
      setDetailItems((prev) => [...prev, { ...item, id: Date.now() }]);
    }
    setShowDetailModal(false);
  };

  const handleEditDetail = (item) => {
    setEditingItem(item);
    setShowDetailModal(true);
  };

  const handleDeleteDetail = async (id) => {
    const res = await confirmDelete({ type: DELETE_ENTITY.plan_detail_row });
    if (res.isConfirmed) {
      setDetailItems((prev) => prev.filter((i) => i.id !== id));
    }
  };

  const typeOptions = [
    { value: "finance", label: "Tài chính" },
    { value: "work", label: "Công việc" },
  ];

  const categoryOptions = [
    { value: "branch", label: "Chi nhánh" },
    { value: "department", label: "Bộ phận" },
    { value: "personal", label: "Cá nhân" },
  ];

  const typeLabel = typeOptions.find((o) => o.value === type)?.label || type;
  const categoryLabel = categoryOptions.find((o) => o.value === category)?.label || category;

  const fieldClass =
    "w-full rounded-2xl border-2 border-gray-100 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:border-red-300 focus:bg-white";

  const labelClass = "mb-1.5 block text-[10px] font-bold uppercase tracking-tight text-gray-400";

  const sectionTitle = (text) => (
    <h3 className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-red-500">
      <div className="h-4 w-1 rounded-full bg-red-500" />
      {text}
    </h3>
  );

  const titleInitial = (title || "K").trim().charAt(0).toUpperCase() || "K";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="plan-create-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
            aria-hidden
          >
            <motion.div
              key="plan-create-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="plan-create-modal-title"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="relative flex shrink-0 items-center justify-between border-b border-gray-100 p-5 sm:p-6">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-red-100 bg-red-50 text-xl font-black text-red-600 shadow-sm sm:h-16 sm:w-16 sm:text-2xl">
                    {titleInitial}
                  </div>
                  <div className="min-w-0">
                    <h2
                      id="plan-create-modal-title"
                      className="text-lg font-bold leading-tight text-gray-800 sm:text-xl"
                    >
                      Tạo kế hoạch mới
                    </h2>
                    <p className="mt-0.5 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Bước {step + 1}/{STEPS.length} — {STEPS[step].title}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => requestClose()}
                  disabled={loading}
                  className="shrink-0 rounded-full p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                  aria-label="Đóng"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Stepper */}
              <div className="shrink-0 border-b border-gray-50 bg-gray-50/40 px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between gap-1 sm:gap-2">
                  {STEPS.map((s, i) => {
                    const done = i < step;
                    const active = i === step;
                    return (
                      <div key={s.id} className="flex min-w-0 flex-1 items-center">
                        <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                          <div
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-black transition-colors sm:h-10 sm:w-10 ${
                              active
                                ? "border-red-600 bg-red-600 text-white shadow-md shadow-red-100"
                                : done
                                  ? "border-red-200 bg-red-50 text-red-600"
                                  : "border-gray-200 bg-white text-gray-400"
                            }`}
                          >
                            {done ? <Check size={18} strokeWidth={3} /> : i + 1}
                          </div>
                          <span
                            className={`block max-w-full text-center text-[7px] font-black uppercase leading-tight tracking-tight sm:text-[8px] ${
                              active ? "text-red-600" : "text-gray-400"
                            }`}
                          >
                            {s.pill}
                          </span>
                        </div>
                        {i < STEPS.length - 1 && (
                          <div
                            className={`mx-0.5 h-0.5 w-full min-w-[8px] max-w-[40px] shrink rounded-full sm:mx-1 ${
                              i < step ? "bg-red-400" : "bg-gray-200"
                            }`}
                            aria-hidden
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-center text-[12px] font-semibold text-gray-500">
                  {STEPS[step].hint}
                </p>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    {step === 0 && (
                      <div className="space-y-5">
                        {sectionTitle("Tiêu đề & phân loại")}
                        <div>
                          <label className={labelClass}>
                            Tiêu đề <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className={fieldClass}
                            placeholder="Ví dụ: Kế hoạch kinh doanh Q2"
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className={labelClass}>
                              Phân loại <span className="text-red-500">*</span>
                            </label>
                            <Select
                              options={categoryOptions}
                              value={categoryOptions.find((opt) => opt.value === category)}
                              onChange={(opt) => setCategory(opt?.value || "")}
                              placeholder="Chọn phân loại"
                              styles={customSelectStyles}
                            />
                          </div>
                          <div>
                            <label className={labelClass}>
                              Loại kế hoạch <span className="text-red-500">*</span>
                            </label>
                            <Select
                              options={typeOptions}
                              value={typeOptions.find((opt) => opt.value === type)}
                              onChange={(opt) => setType(opt?.value || "")}
                              placeholder="Chọn loại"
                              styles={customSelectStyles}
                            />
                          </div>
                        </div>

                        {sectionTitle("Khung thời gian")}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className={labelClass}>
                              Từ ngày <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <CalendarRange
                                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                aria-hidden
                              />
                              <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={`${fieldClass} pl-10`}
                              />
                            </div>
                          </div>
                          <div>
                            <label className={labelClass}>
                              Đến ngày <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <CalendarRange
                                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                aria-hidden
                              />
                              <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={`${fieldClass} pl-10`}
                              />
                            </div>
                          </div>
                        </div>

                        {sectionTitle("Ngân sách dự kiến")}
                        <div className="rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 via-white to-red-50/30 p-4 shadow-sm sm:p-5">
                          <div className="flex items-center gap-2 text-red-600">
                            <div className="rounded-lg bg-white/80 p-2 shadow-sm">
                              <Banknote size={20} strokeWidth={2.25} />
                            </div>
                            <span className="text-[11px] font-black uppercase tracking-widest text-red-600">
                              Ước tính (VND)
                            </span>
                          </div>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={budget}
                            onChange={(e) => setBudget(formatCurrencyInput(e.target.value))}
                            className="mt-3 w-full border-0 bg-transparent text-2xl font-black tracking-tight text-red-700 outline-none placeholder:text-red-200 sm:text-3xl"
                            placeholder="0"
                          />
                          <p className="mt-2 text-[12px] font-semibold leading-relaxed text-gray-600">
                            {(() => {
                              const n = Number(parseBudgetDigits(budget)) || 0;
                              if (n <= 0) {
                                return (
                                  <span className="text-[11px] text-gray-500">
                                    Có thể bỏ trống nếu chưa xác định.
                                  </span>
                                );
                              }
                              const words = numberToVietnameseText(n);
                              return (
                                <>
                                  <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                                    Bằng chữ:{" "}
                                  </span>
                                  <span className="text-[13px] font-bold text-gray-800">{words}</span>
                                </>
                              );
                            })()}
                          </p>
                        </div>
                      </div>
                    )}

                    {step === 1 && (
                      <div className="space-y-5">
                        <div className="rounded-2xl border border-red-100 bg-red-50 p-4">
                          <p className="text-[12px] font-semibold leading-relaxed text-red-800">
                            <span className="font-black">Mẹo:</span> mô tả rõ mục đích, phạm vi và kết quả
                            mong đợi — giúp người phê duyệt nắm nhanh bối cảnh.
                          </p>
                        </div>
                        {sectionTitle("Nội dung tổng quan")}
                        <div>
                          <div className="mb-1.5 flex items-center justify-between gap-2">
                            <span className="block text-[10px] font-bold uppercase tracking-tight text-gray-400">
                              Nội dung <span className="text-red-500">*</span>
                            </span>
                            <span
                              className={`text-[10px] font-bold tabular-nums ${
                                content.length >= PLAN_CONTENT_MAX_LENGTH
                                  ? "text-red-600"
                                  : "text-gray-400"
                              }`}
                            >
                              {content.length}/{PLAN_CONTENT_MAX_LENGTH}
                            </span>
                          </div>
                          <textarea
                            rows={4}
                            maxLength={PLAN_CONTENT_MAX_LENGTH}
                            value={content}
                            onChange={(e) =>
                              setContent(e.target.value.slice(0, PLAN_CONTENT_MAX_LENGTH))
                            }
                            className={`${fieldClass} min-h-[90px] resize-y`}
                            placeholder="Tóm tắt kế hoạch, bối cảnh, định hướng…"
                          />
                        </div>
                        {sectionTitle("Tệp đính kèm")}
                        <div
                          role="presentation"
                          onDragEnter={onMainDragEnter}
                          onDragLeave={onMainDragLeave}
                          onDragOver={onMainDragOver}
                          onDrop={onMainDrop}
                          className={`rounded-2xl border-2 border-dashed bg-gray-50/50 px-3 py-3 text-center transition sm:flex sm:items-center sm:justify-center sm:gap-4 sm:py-2.5 sm:text-left ${
                            mainFileDragOver
                              ? "border-red-500 bg-red-100/40 ring-2 ring-red-200/50"
                              : "border-gray-200 hover:border-red-200 hover:bg-red-50/20"
                          }`}
                        >
                          <input
                            ref={planMainFileRef}
                            type="file"
                            id="planCreateFile"
                            accept="application/pdf,.pdf"
                            onChange={handleMainPlanFileChange}
                            className="hidden"
                          />
                          <div className="mx-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-red-600 shadow-sm sm:mx-0">
                            <Upload className="h-5 w-5" strokeWidth={2.25} aria-hidden />
                          </div>
                          <label
                            htmlFor="planCreateFile"
                            className="mt-2 flex cursor-pointer flex-col items-center gap-0.5 sm:mt-0 sm:min-w-0 sm:flex-1 sm:items-start"
                          >
                            <span className="text-xs font-black uppercase tracking-tight text-red-600">
                              Kéo thả PDF vào đây hoặc chọn tệp
                            </span>
                            <span className="text-[10px] font-semibold text-gray-500">
                              Chỉ PDF · tối đa 25 MB · tuỳ chọn
                            </span>
                          </label>
                          {attachment && (
                            <p className="mt-2 flex items-center justify-center gap-1.5 border-t border-gray-100 pt-2 text-[11px] font-bold text-gray-800 sm:mt-0 sm:border-t-0 sm:border-l sm:pl-3 sm:pt-0">
                              <Paperclip size={14} className="shrink-0 text-red-500" />
                              <span className="truncate">{attachment.name}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-end justify-between gap-3">
                          <div>
                            {sectionTitle("Danh sách mục tiêu")}
                            <p className="-mt-1 text-[12px] font-medium text-gray-500">
                              Mỗi mục tiêu có thể kèm tệp riêng.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingItem(null);
                              setShowDetailModal(true);
                            }}
                            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow-lg shadow-red-100 transition hover:bg-red-700 active:scale-[0.98]"
                          >
                            <Plus size={18} strokeWidth={2.5} />
                            Thêm mục tiêu
                          </button>
                        </div>

                        {detailItems.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-red-200 bg-red-50/40 p-8 text-center">
                            <Target className="mx-auto mb-3 h-10 w-10 text-red-300" />
                            <p className="text-sm font-semibold text-red-800">Chưa có mục tiêu nào</p>
                            <p className="mt-1 text-[12px] text-red-700/80">
                              Nhấn &quot;Thêm mục tiêu&quot; để bắt đầu.
                            </p>
                          </div>
                        ) : (
                          <ul className="space-y-2">
                            {detailItems.map((item, idx) => (
                              <li
                                key={item.id}
                                className="rounded-2xl border border-red-100 bg-red-50 p-4 transition hover:border-red-200"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-[10px] font-black uppercase tracking-tight text-red-700">
                                      Mục tiêu {idx + 1}
                                    </p>
                                    <p className="mt-1 text-[13px] font-semibold text-gray-900">{item.goal}</p>
                                    <p className="mt-1.5 text-[12px] text-gray-600">
                                      <span className="font-bold text-gray-500">Phụ trách:</span> {item.owner}
                                    </p>
                                    {item.attachment && (
                                      <p className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-red-700">
                                        <Paperclip size={12} />
                                        {item.attachment instanceof File
                                          ? item.attachment.name
                                          : String(item.attachment)}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex shrink-0 gap-1">
                                    <button
                                      type="button"
                                      onClick={() => handleEditDetail(item)}
                                      className="rounded-lg p-2 text-gray-500 transition hover:bg-white hover:text-red-600"
                                      aria-label="Sửa"
                                    >
                                      <Pencil size={18} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteDetail(item.id)}
                                      className="rounded-lg p-2 text-gray-500 transition hover:bg-white hover:text-red-600"
                                      aria-label="Xoá"
                                    >
                                      <Trash2 size={18} />
                                    </button>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-5">
                        <div className="rounded-2xl border border-red-100 bg-red-50/60 p-4">
                          <div className="flex items-center gap-2 text-red-800">
                            <ClipboardList size={20} className="shrink-0" />
                            <p className="text-[12px] font-bold leading-snug">
                              Kiểm tra lại thông tin. Sau khi gửi phê duyệt, bạn có thể theo dõi trạng thái trong danh
                              sách kế hoạch.
                            </p>
                          </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                            <p className="text-[10px] font-bold uppercase text-gray-400">Tiêu đề</p>
                            <p className="mt-1 text-sm font-bold text-gray-900">{title || "—"}</p>
                            <p className="mt-3 text-[10px] font-bold uppercase text-gray-400">Phân loại</p>
                            <p className="mt-1 text-[13px] font-semibold text-gray-700">{categoryLabel}</p>
                            <p className="mt-3 text-[10px] font-bold uppercase text-gray-400">Loại</p>
                            <p className="mt-1 text-[13px] font-semibold text-gray-700">{typeLabel}</p>
                          </div>
                          <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                            <p className="text-[10px] font-bold uppercase text-gray-400">Thời gian</p>
                            <p className="mt-1 text-[13px] font-semibold text-gray-800">
                              {startDate} → {endDate}
                            </p>
                            <p className="mt-3 text-[10px] font-bold uppercase text-gray-400">Ngân sách dự kiến</p>
                            <p className="mt-1 text-lg font-black text-red-700">{formatCurrencyDisplay(budget)}</p>
                            <p className="mt-3 text-[10px] font-bold uppercase text-gray-400">Tệp chính</p>
                            <p className="mt-1 text-[13px] font-semibold text-gray-700">
                              {attachment?.name || "Không có"}
                            </p>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                          <p className="text-[10px] font-bold uppercase text-gray-400">Nội dung tổng quan</p>
                          <p className="mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap text-[13px] font-medium leading-relaxed text-gray-700">
                            {content}
                          </p>
                        </div>

                        <div>
                          <p className="mb-2 text-[10px] font-bold uppercase tracking-tight text-gray-400">
                            {detailItems.length} mục tiêu
                          </p>
                          <div className="max-h-40 space-y-2 overflow-y-auto">
                            {detailItems.map((item, idx) => (
                              <div
                                key={item.id}
                                className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-[12px] font-semibold text-gray-800"
                              >
                                {idx + 1}. {item.goal}
                                <span className="block text-[11px] font-medium text-gray-600">{item.owner}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Footer actions */}
              <div className="shrink-0 border-t border-gray-100 bg-white p-4 sm:flex sm:items-center sm:justify-between sm:gap-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={loading || !hasDraftInput()}
                    title={
                      hasDraftInput()
                        ? "Xoá toàn bộ nội dung và bắt đầu lại từ bước 1"
                        : "Chưa có dữ liệu để làm sạch"
                    }
                    onClick={() => handleClearForm()}
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:pointer-events-none disabled:opacity-40"
                  >
                    <RotateCcw size={18} strokeWidth={2.25} />
                    Làm sạch
                  </button>
                  {step > 0 && (
                    <button
                      type="button"
                      disabled={loading}
                      onClick={goBack}
                      className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronLeft size={18} />
                      Quay lại
                    </button>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-end gap-2 sm:mt-0">
                  {step < 3 ? (
                    <button
                      type="button"
                      disabled={loading}
                      onClick={goNext}
                      className="inline-flex items-center gap-1 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-red-100 transition hover:bg-red-700 active:scale-[0.98] disabled:opacity-50"
                    >
                      Tiếp tục
                      <ChevronRight size={18} />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => handleSave("draft")}
                        className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={18} />}
                        Lưu nháp
                      </button>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => handleSave("waiting_approval")}
                        className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-red-100 transition hover:bg-red-700 disabled:opacity-50"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={18} />}
                        Gửi phê duyệt
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>

            <PlanDetailModal
              isOpen={showDetailModal}
              onClose={() => {
                setShowDetailModal(false);
                setEditingItem(null);
              }}
              onSave={handleAddDetail}
              initialData={editingItem}
              planCategory={category}
              defaultExecutorName={defaultExecutorName}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PlanCreateModal;
